/**
 * Sprint T-16.1 · Automation Center · JobCard.
 *
 * Single job card in the JobsGrid. All actions delegated to props · this
 * component does NOT call the API directly.
 */
import React from "react";
import { Play, Pause, PlayCircle, ChevronRight, Link2 } from "lucide-react";
import { CategoryBadge, StatusBadge, PriorityBadge } from "./Badges";
import { RelativeTime, ScheduleHumanReadable } from "./Formatters";

function _isPaused(state) {
  if (!state?.paused_until) return false;
  try {
    return new Date(state.paused_until).getTime() > Date.now();
  } catch {
    return false;
  }
}

export default function JobCard({ job, onRun, onPause, onResume, onDetail }) {
  const state = job.state || {};
  const paused = _isPaused(state);
  const testIdBase = `automation-job-${job.job_id}`;
  const consecFail = Number(state.consecutive_failures || 0);

  return (
    <article
      data-testid={testIdBase}
      className="flex flex-col gap-2 rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] p-3 transition hover:border-[var(--ag-gold)]/40"
    >
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <CategoryBadge category={job.category} testId={`${testIdBase}-category`} />
            <PriorityBadge priority={job.priority} testId={`${testIdBase}-priority`} />
            {state.last_status && (
              <StatusBadge status={state.last_status} testId={`${testIdBase}-last-status`} />
            )}
          </div>
          <h3
            data-testid={`${testIdBase}-name`}
            className="mt-1.5 truncate font-mono text-xs font-semibold text-[var(--ag-text)]"
            title={job.name}
          >
            {job.name}
          </h3>
          <p className="mt-0.5 truncate text-[10px] text-[var(--ag-text-dim)]" title={job.description}>
            {job.description}
          </p>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-1.5 text-[10px]">
        <div>
          <div className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Schedule</div>
          <ScheduleHumanReadable schedule={job.schedule} testId={`${testIdBase}-schedule`} />
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">Última</div>
          <RelativeTime iso={state.last_run_at} testId={`${testIdBase}-last-run`} />
        </div>
        {job.depends_on?.length > 0 && (
          <div className="col-span-2">
            <div className="text-[9px] uppercase tracking-wide text-[var(--ag-text-dim)]">
              <Link2 className="mr-0.5 inline h-2.5 w-2.5" /> depends_on
            </div>
            <div className="mt-0.5 flex flex-wrap gap-1">
              {job.depends_on.map((dep) => (
                <code
                  key={dep}
                  className="rounded bg-[var(--ag-surface-2)] px-1 py-0.5 font-mono text-[9px] text-[var(--ag-text-dim)]"
                >
                  {dep}
                </code>
              ))}
            </div>
          </div>
        )}
        {(state.total_runs != null || consecFail > 0) && (
          <div className="col-span-2 flex items-center gap-2 border-t border-[var(--ag-border)] pt-1 text-[10px]">
            <span className="text-[var(--ag-text-dim)]">
              runs: <span className="font-mono">{state.total_runs || 0}</span>
            </span>
            <span className="text-emerald-500/80">
              ok: <span className="font-mono">{state.total_successes || 0}</span>
            </span>
            <span className="text-red-500/80">
              fail: <span className="font-mono">{state.total_failures || 0}</span>
            </span>
            {consecFail > 0 && (
              <span
                data-testid={`${testIdBase}-consec-fail`}
                className="ml-auto rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 font-mono text-amber-500"
                title="Fallos consecutivos"
              >
                ⚠ {consecFail} consec.
              </span>
            )}
          </div>
        )}
      </div>

      {paused && (
        <div
          data-testid={`${testIdBase}-paused-banner`}
          className="rounded border border-slate-500/30 bg-slate-500/5 px-2 py-1 text-[10px] text-slate-400"
        >
          Pausado hasta <span className="font-mono">{state.paused_until?.slice(0, 16)}</span>
        </div>
      )}

      <footer className="mt-auto flex flex-wrap items-center justify-end gap-1 border-t border-[var(--ag-border)] pt-2">
        <button
          type="button"
          data-testid={`${testIdBase}-run-btn`}
          onClick={() => onRun?.(job)}
          className="inline-flex items-center gap-1 rounded border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[10px] font-medium text-emerald-500 hover:bg-emerald-500/20"
        >
          <Play className="h-3 w-3" /> Ejecutar
        </button>
        {paused ? (
          <button
            type="button"
            data-testid={`${testIdBase}-resume-btn`}
            onClick={() => onResume?.(job)}
            className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-2 py-1 text-[10px] hover:bg-[var(--ag-surface-2)]"
          >
            <PlayCircle className="h-3 w-3" /> Reanudar
          </button>
        ) : (
          <button
            type="button"
            data-testid={`${testIdBase}-pause-btn`}
            onClick={() => onPause?.(job)}
            className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-2 py-1 text-[10px] hover:bg-[var(--ag-surface-2)]"
          >
            <Pause className="h-3 w-3" /> Pausar
          </button>
        )}
        <button
          type="button"
          data-testid={`${testIdBase}-detail-btn`}
          onClick={() => onDetail?.(job)}
          className="inline-flex items-center gap-1 rounded border border-[var(--ag-border)] px-2 py-1 text-[10px] hover:bg-[var(--ag-surface-2)]"
        >
          Detalle <ChevronRight className="h-3 w-3" />
        </button>
      </footer>
    </article>
  );
}
